import React from "react";
import { motion } from "framer-motion";
import {
  Lock,
  CheckCircle,
  Award,
  Globe,
  Factory,
  Users,
  TrendingUp,
  FileCheck,
  ArrowRight,
  Mail,
  ExternalLink,
  ClipboardList,
  ShieldCheck,
  Target,
  BarChart3,
  Building2,
  Server,
  Wifi,
  Database,
  Eye,
  KeyRound,
} from "lucide-react";
import Navbar from "../../../components/Navbar";
import Footer from "../../../components/Footer";

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0 },
};

const ISO27001_Details = () => {
  return (
    <>
      <Navbar />

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-[#0a1628] via-[#132744] to-[#1a3a5c] text-white py-24 px-4 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-10 w-72 h-72 bg-cyan-400 rounded-full blur-3xl" />
          <div className="absolute bottom-10 right-10 w-96 h-96 bg-blue-600 rounded-full blur-3xl" />
        </div>
        <motion.div
          initial="hidden"
          animate="visible"
          variants={fadeUp}
          transition={{ duration: 0.7 }}
          className="relative max-w-5xl mx-auto text-center"
        >
          <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full text-sm mb-6">
            <Lock size={18} className="text-cyan-300" />
            <span>ISO Lead Auditor Training</span>
          </div>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
            ISO/IEC 27001 Certification
          </h1>
          <p className="text-lg md:text-xl text-gray-200 max-w-3xl mx-auto leading-relaxed">
            Information Security Management System (ISMS) — The internationally
            recognized standard for protecting sensitive information and
            managing cybersecurity risks systematically.
          </p>
        </motion.div>
      </section>

      {/* Overview Section */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              ISO/IEC 27001 Certification Overview
            </h2>
            <div className="w-20 h-1 bg-[var(--bg-primary)] rounded-full mb-8" />
            <p className="text-gray-600 text-lg leading-relaxed mb-4">
              ISO/IEC 27001 is an internationally recognized standard for
              Information Security Management Systems (ISMS). It provides a
              systematic approach to protecting sensitive information, including
              financial data, intellectual property, personal data, and customer
              information.
            </p>
            <p className="text-gray-600 text-lg leading-relaxed">
              The standard helps organizations identify information security
              risks and implement appropriate controls to ensure
              confidentiality, integrity, and availability (CIA) of information.
              ISO/IEC 27001 is applicable to organizations of all sizes and
              sectors, especially those handling critical or sensitive data.
            </p>
          </motion.div>
        </div>
      </section>

      {/* What Is ISO/IEC 27001 */}
      <section className="py-16 px-4 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-6">
              What Is ISO/IEC 27001 Certification?
            </h2>
            <div className="w-20 h-1 bg-[var(--bg-primary)] rounded-full mb-8" />
            <p className="text-gray-600 text-lg leading-relaxed mb-8">
              ISO/IEC 27001 certification confirms that an organization has
              implemented an effective Information Security Management System
              (ISMS) to manage information security risks in a structured and
              proactive manner.
            </p>
            <p className="text-gray-700 font-semibold text-lg mb-4">
              The certification focuses on:
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {[
                {
                  icon: Target,
                  text: "Risk-based information security management",
                },
                {
                  icon: Lock,
                  text: "Protection of confidential and sensitive information",
                },
                {
                  icon: ShieldCheck,
                  text: "Legal, regulatory, and contractual compliance",
                },
                {
                  icon: Server,
                  text: "Cybersecurity and data protection controls",
                },
                {
                  icon: TrendingUp,
                  text: "Continual improvement of information security performance",
                },
              ].map((item, i) => (
                <motion.div
                  key={i}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={fadeUp}
                  transition={{ duration: 0.4, delay: i * 0.1 }}
                  className="flex items-center gap-3 bg-white p-4 rounded-xl shadow-sm border border-gray-100"
                >
                  <item.icon
                    size={22}
                    className="text-[var(--bg-primary)] shrink-0"
                  />
                  <span className="text-gray-700 font-medium">{item.text}</span>
                </motion.div>
              ))}
            </div>
            <p className="text-gray-600 text-base mt-6 italic">
              ISO/IEC 27001 is often a mandatory or preferred requirement for IT
              companies, service providers, and organizations working with
              international clients.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Key Features */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Key Features of ISO/IEC 27001 Certification
            </h2>
            <div className="w-20 h-1 bg-[var(--bg-primary)] rounded-full mx-auto" />
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              {
                icon: Lock,
                title: "Information Security Assurance",
                desc: "Protects data against unauthorized access, breaches, and misuse.",
              },
              {
                icon: Target,
                title: "Risk-Based Approach",
                desc: "Identifies, assesses, and treats information security risks effectively.",
              },
              {
                icon: ShieldCheck,
                title: "Regulatory & Legal Compliance",
                desc: "Supports compliance with data protection and privacy regulations.",
              },
              {
                icon: Server,
                title: "Business Continuity & Resilience",
                desc: "Enhances preparedness against cyber incidents and disruptions.",
              },
              {
                icon: Globe,
                title: "Integration with Other ISO Standards",
                desc: "Easily integrates with ISO 9001, ISO 14001, and ISO 22301.",
              },
            ].map((feature, i) => (
              <motion.div
                key={i}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeUp}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                className="group bg-gray-50 hover:bg-white p-6 rounded-2xl border border-gray-100 hover:shadow-xl transition-all duration-300"
              >
                <div className="w-12 h-12 bg-[var(--bg-primary)] rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                  <feature.icon size={24} className="text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  {feature.title}
                </h3>
                <p className="text-gray-600">{feature.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* What is ISMS */}
      <section className="py-16 px-4 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-6">
              What Is an Information Security Management System (ISMS)?
            </h2>
            <div className="w-20 h-1 bg-[var(--bg-primary)] rounded-full mb-8" />
            <p className="text-gray-600 text-lg leading-relaxed mb-6">
              An Information Security Management System (ISMS) is a structured
              framework that enables organizations to:
            </p>
            <div className="space-y-3 mb-6">
              {[
                "Identify information assets and security risks",
                "Implement appropriate security controls",
                "Monitor and review security performance",
                "Respond to incidents and breaches effectively",
                "Continuously improve information security processes",
              ].map((point, i) => (
                <motion.div
                  key={i}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={fadeUp}
                  transition={{ duration: 0.4, delay: i * 0.1 }}
                  className="flex items-start gap-3 bg-white p-4 rounded-xl shadow-sm border border-gray-100"
                >
                  <CheckCircle
                    size={20}
                    className="text-[var(--bg-primary)] mt-0.5 shrink-0"
                  />
                  <span className="text-gray-700">{point}</span>
                </motion.div>
              ))}
            </div>
            <p className="text-gray-600 text-lg leading-relaxed">
              An effective ISMS ensures information security is embedded into
              daily operations and strategic decision-making.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Importance for Businesses */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-6">
              Importance of ISO/IEC 27001 Certification for Businesses
            </h2>
            <div className="w-20 h-1 bg-[var(--bg-primary)] rounded-full mb-8" />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {[
                "Protects sensitive and confidential information",
                "Reduces risk of data breaches and cyber threats",
                "Enhances customer, partner, and stakeholder trust",
                "Improves compliance with legal and contractual obligations",
                "Strengthens cybersecurity governance and accountability",
                "Supports business continuity and risk management",
              ].map((point, i) => (
                <motion.div
                  key={i}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={fadeUp}
                  transition={{ duration: 0.4, delay: i * 0.05 }}
                  className="flex items-start gap-3 p-4 rounded-xl bg-gray-50 border border-gray-100"
                >
                  <ArrowRight
                    size={18}
                    className="text-[var(--bg-primary)] mt-1 shrink-0"
                  />
                  <span className="text-gray-700">{point}</span>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* ISMS Certification */}
      <section className="py-16 px-4 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-6">
              What Is ISMS Certification?
            </h2>
            <div className="w-20 h-1 bg-[var(--bg-primary)] rounded-full mb-8" />
            <p className="text-gray-600 text-lg leading-relaxed mb-6">
              ISO/IEC 27001 certification is granted by an accredited
              certification body after a comprehensive audit of:
            </p>
            <div className="space-y-3 mb-6">
              {[
                "ISMS documentation and policies",
                "Risk assessment and risk treatment plans",
                "Technical and organizational security controls",
                "Incident management and business continuity",
                "Training, internal audits, and management reviews",
              ].map((point, i) => (
                <motion.div
                  key={i}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={fadeUp}
                  transition={{ duration: 0.4, delay: i * 0.1 }}
                  className="flex items-start gap-3 bg-white p-4 rounded-xl shadow-sm border border-gray-100"
                >
                  <FileCheck
                    size={20}
                    className="text-[var(--bg-primary)] mt-0.5 shrink-0"
                  />
                  <span className="text-gray-700">{point}</span>
                </motion.div>
              ))}
            </div>
            <p className="text-gray-600 text-lg leading-relaxed">
              Certification confirms that the organization complies with ISO/IEC
              27001 requirements and effectively manages information security
              risks.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Significance */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-6">
              Significance of ISO/IEC 27001 Certification
            </h2>
            <div className="w-20 h-1 bg-[var(--bg-primary)] rounded-full mb-8" />
            <p className="text-gray-700 font-semibold text-lg mb-4">
              ISO/IEC 27001 helps organizations:
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {[
                "Demonstrate commitment to information security",
                "Meet client, regulator, and contractual requirements",
                "Improve control over information assets",
                "Enhance resilience against cyber threats",
                "Strengthen reputation in domestic and international markets",
              ].map((point, i) => (
                <motion.div
                  key={i}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={fadeUp}
                  transition={{ duration: 0.4, delay: i * 0.05 }}
                  className="flex items-start gap-3 p-4 rounded-xl bg-gray-50 border border-gray-100"
                >
                  <CheckCircle
                    size={18}
                    className="text-[var(--bg-primary)] mt-1 shrink-0"
                  />
                  <span className="text-gray-700">{point}</span>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Advantages */}
      <section className="py-16 px-4 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-6">
              Advantages of ISO/IEC 27001 Certification
            </h2>
            <div className="w-20 h-1 bg-[var(--bg-primary)] rounded-full mb-8" />
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {[
                "Strong protection of data and information assets",
                "Reduced risk of cyber incidents and breaches",
                "Increased customer and partner confidence",
                "Improved compliance with data protection laws",
                "Competitive advantage in global markets",
                "Better risk management and decision-making",
                "Continuous improvement in information security",
              ].map((adv, i) => (
                <motion.div
                  key={i}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={fadeUp}
                  transition={{ duration: 0.4, delay: i * 0.05 }}
                  className="bg-white p-5 rounded-2xl shadow-sm border border-gray-100 hover:shadow-md transition-shadow"
                >
                  <CheckCircle
                    size={20}
                    className="text-[var(--bg-primary)] mb-3"
                  />
                  <p className="text-gray-700 font-medium text-sm">{adv}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Industries */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-6">
              Industries That Require ISO/IEC 27001 Certification
            </h2>
            <div className="w-20 h-1 bg-[var(--bg-primary)] rounded-full mb-8" />
            <p className="text-gray-600 text-lg leading-relaxed mb-8">
              ISO/IEC 27001 is essential for organizations in:
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {[
                {
                  icon: Server,
                  name: "Information Technology & Software Development",
                },
                { icon: Database, name: "Cloud Services and Data Centers" },
                { icon: Wifi, name: "Telecommunications" },
                { icon: Building2, name: "Banking, Finance, and FinTech" },
                {
                  icon: ShieldCheck,
                  name: "Healthcare and Medical Data Services",
                },
                { icon: Globe, name: "E-commerce and Digital Platforms" },
                { icon: Building2, name: "Government and Public Sector" },
                { icon: Users, name: "Consulting and Outsourcing Services" },
              ].map((industry, i) => (
                <motion.div
                  key={i}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={fadeUp}
                  transition={{ duration: 0.4, delay: i * 0.05 }}
                  className="flex items-center gap-3 bg-gray-50 p-4 rounded-xl border border-gray-100 hover:shadow-md transition-shadow"
                >
                  <industry.icon
                    size={22}
                    className="text-[var(--bg-primary)] shrink-0"
                  />
                  <span className="text-gray-700 font-medium">
                    {industry.name}
                  </span>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Applications */}
      <section className="py-16 px-4 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-6">
              Applications of ISO/IEC 27001 in Industry
            </h2>
            <div className="w-20 h-1 bg-[var(--bg-primary)] rounded-full mb-8" />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {[
                "Data protection and privacy management",
                "Cybersecurity risk management",
                "Access control and identity management",
                "Incident response and breach handling",
                "Business continuity and disaster recovery",
                "Secure IT infrastructure and cloud operations",
              ].map((app, i) => (
                <motion.div
                  key={i}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={fadeUp}
                  transition={{ duration: 0.4, delay: i * 0.05 }}
                  className="flex items-start gap-3 p-4 rounded-xl bg-white border border-gray-100 shadow-sm"
                >
                  <ArrowRight
                    size={18}
                    className="text-[var(--bg-primary)] mt-1 shrink-0"
                  />
                  <span className="text-gray-700">{app}</span>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* How to Get Certified */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-6">
              How to Get ISO/IEC 27001 Certification
            </h2>
            <div className="w-20 h-1 bg-[var(--bg-primary)] rounded-full mb-8" />
            <p className="text-gray-700 font-semibold text-lg mb-6">
              The certification process typically includes:
            </p>
            <div className="space-y-4">
              {[
                "Conducting an Information Security Gap Analysis",
                "Identifying information assets and risks",
                "Performing risk assessment and risk treatment",
                "Developing ISMS policies, procedures, and controls",
                "Implementing technical and organizational security measures",
                "Training employees on information security awareness",
                "Conducting internal audits and management reviews",
                "Certification audit by an accredited certification body",
              ].map((step, i) => (
                <motion.div
                  key={i}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={fadeUp}
                  transition={{ duration: 0.4, delay: i * 0.08 }}
                  className="flex items-start gap-4 bg-gray-50 p-5 rounded-2xl shadow-sm border border-gray-100"
                >
                  <div className="w-8 h-8 bg-[var(--bg-primary)] rounded-full flex items-center justify-center shrink-0">
                    <span className="text-white font-bold text-sm">
                      {i + 1}
                    </span>
                  </div>
                  <span className="text-gray-700 text-lg">{step}</span>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Why Choose MBA Solutions */}
      <section className="py-16 px-4 bg-gradient-to-br from-[#0a1628] via-[#132744] to-[#1a3a5c] text-white">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Why Choose MBA Solutions for ISO/IEC 27001 Certification?
            </h2>
            <div className="w-20 h-1 bg-cyan-400 rounded-full mx-auto mb-6" />
            <p className="text-gray-200 text-lg leading-relaxed max-w-4xl mx-auto">
              MBA Solutions Pvt. Ltd. provides comprehensive ISO/IEC 27001
              consultancy services, helping organizations protect their
              information assets and achieve certification smoothly.
            </p>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="mb-10"
          >
            <p className="text-gray-200 font-semibold text-lg text-center mb-6">
              Our services include:
            </p>
            <div className="flex flex-wrap justify-center gap-3">
              {[
                "ISMS design and implementation",
                "Risk assessment and control selection",
                "Documentation and policy development",
                "Internal and mock audits",
                "Certification body coordination and audit support",
              ].map((service, i) => (
                <span
                  key={i}
                  className="bg-white/10 backdrop-blur-sm px-5 py-2 rounded-full text-sm font-medium"
                >
                  {service}
                </span>
              ))}
            </div>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="bg-white/10 backdrop-blur-sm rounded-3xl p-8 md:p-10 text-center"
          >
            <div className="flex flex-col sm:flex-row items-center justify-center gap-6 mb-6">
              <a
                href="mailto:atif@mbasolutions.co"
                className="flex items-center gap-2 text-cyan-300 hover:text-cyan-200 transition-colors"
              >
                <Mail size={20} />
                <span>atif@mbasolutions.co</span>
              </a>
              <a
                href="https://www.mbasolutions.co"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 text-cyan-300 hover:text-cyan-200 transition-colors"
              >
                <ExternalLink size={20} />
                <span>www.mbasolutions.co</span>
              </a>
            </div>
            <p className="text-gray-200 text-base leading-relaxed max-w-3xl mx-auto">
              We support organizations in achieving robust cybersecurity,
              regulatory compliance, and international client trust through
              effective ISO/IEC 27001 implementation.
            </p>
          </motion.div>
        </div>
      </section>

      <Footer />
    </>
  );
};

export default ISO27001_Details;
